import { faVideo } from '@fortawesome/pro-regular-svg-icons/faVideo';
import { faVideoPlus } from '@fortawesome/pro-regular-svg-icons/faVideoPlus';
import { faUpRightAndDownLeftFromCenter } from '@fortawesome/pro-solid-svg-icons/faUpRightAndDownLeftFromCenter';
import { Property } from '@propertypal/shared/src/types/property';
import { isWhite } from '@propertypal/shared/src/utils/colour';
import { getGalleryImages, getThumbImages } from '@propertypal/shared/src/utils/property';
import getPropertyTour from '@propertypal/shared/src/utils/property/getPropertyTour';
import getPropertyVideos from '@propertypal/shared/src/utils/property/getPropertyVideos';
import getSellingAgent, { getAgentColours } from '@propertypal/shared/src/utils/property/getSellingAgent';
import PropertyBanner from '@propertypal/web-ui/src/property-box/PropertyBanner';
import React, { FunctionComponent } from 'react';
import { useTheme } from 'styled-components';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import useMediaQuery from '../media-query/useMediaQuery';
import {
  Container,
  Expander,
  HoverOverlay,
  LargeThumb,
  MediaOverlay,
  MoreOverlay,
  SmallThumb,
  SmallThumbs,
} from './PropertyThumbTiles.style';

type OverlayType = 'VIDEO' | 'TOUR' | 'MORE';

interface Props {
  property: Property;
  onClick: (index: number, type: OverlayType) => void;
}

const isCropped = (width: number, height: number) => {
  const targetRatio = 4 / 3;
  const actualRatio = width / height;
  const margin = 0.15;

  return Math.abs(actualRatio - targetRatio) > margin;
};

const getOverlay = (overlays: OverlayType[], index: number, totalThumbs: number, totalImages: number) => {
  const oIndex = totalThumbs - index - 1;

  if (oIndex < overlays.length) {
    if (overlays[oIndex] === 'VIDEO') {
      return (
        <MediaOverlay>
          <FontAwesomeIcon icon={faVideo} style={{ fontSize: 40, color: 'white' }} />
          <p>View Video</p>
        </MediaOverlay>
      );
    }

    if (overlays[oIndex] === 'TOUR') {
      return (
        <MediaOverlay>
          <FontAwesomeIcon icon={faVideoPlus} style={{ fontSize: 40, color: 'white' }} />
          <p>View Virtual Tour</p>
        </MediaOverlay>
      );
    }

    if (overlays[oIndex] === 'MORE') {
      return <MoreOverlay>+{totalImages - totalThumbs}</MoreOverlay>;
    }
  }

  return null;
};

const PropertyThumbTiles: FunctionComponent<Props> = (props) => {
  const images = getGalleryImages(props.property, 'hd');
  const thumbImages = getThumbImages(props.property, 4, 'hd');
  const theme = useTheme();
  const sellingAgent = getSellingAgent(props.property);
  const { pdpBrandingBgColour } = getAgentColours(props.property.enhancedBranding, sellingAgent);

  const propertyVideo = getPropertyVideos(props.property);
  const propertyTour = getPropertyTour(props.property);
  const isMobile = useMediaQuery({ query: '(max-width: 980px)' });
  const overlays: OverlayType[] = [];
  const totalThumbs = !isMobile && thumbImages.length >= 4 ? 4 : thumbImages.length >= 2 ? 2 : 1;
  if ((isMobile && images.length > 3) || (!isMobile && images.length > 5)) overlays.push('MORE');
  if (propertyVideo) overlays.push('VIDEO');
  if (propertyTour) overlays.push('TOUR');

  const handleImageClick = (index: number, originalIndex: number) => () => {
    if (props.property.images) {
      const oIndex = totalThumbs - index - 1;
      const type = oIndex < overlays.length ? overlays[oIndex] : 'MORE';
      props.onClick(Math.min(originalIndex, props.property.images.length - 1), type);
    }
  };

  if (images.length === 0) {
    return null;
  }

  return (
    <Container
      style={{
        borderTop:
          props.property.enhancedBranding && !isWhite(pdpBrandingBgColour) ? undefined : `1px solid ${theme.keyline}`,
      }}
    >
      <LargeThumb onClick={handleImageClick(0, 0)} data-testid="property-main-thumb">
        <img src={images[0].image} alt={props.property.displayAddress} />
        <HoverOverlay />

        <PropertyBanner property={props.property} />

        {isCropped(images[0].width, images[0].height) && (
          <Expander data-testid="expander-icon">
            <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} color={theme.white} size={20} />
          </Expander>
        )}
      </LargeThumb>

      {thumbImages.length >= 2 && (
        <SmallThumbs $total={thumbImages.length}>
          <SmallThumb $total={thumbImages.length} onClick={handleImageClick(0, thumbImages[0].originalIndex)}>
            <img src={thumbImages[0]?.url} alt={props.property.displayAddress} />
            <HoverOverlay />
            {getOverlay(overlays, 0, totalThumbs, images.length)}
          </SmallThumb>
          <SmallThumb $total={thumbImages.length} onClick={handleImageClick(1, thumbImages[1].originalIndex)}>
            <img src={thumbImages[1]?.url} alt={props.property.displayAddress} />
            <HoverOverlay />
            {getOverlay(overlays, 1, totalThumbs, images.length)}
          </SmallThumb>

          {thumbImages.length >= 4 && (
            <>
              <SmallThumb $total={thumbImages.length} onClick={handleImageClick(2, thumbImages[2].originalIndex)}>
                <img src={thumbImages[2]?.url} alt={props.property.displayAddress} />
                <HoverOverlay />
                {getOverlay(overlays, 2, totalThumbs, images.length)}
              </SmallThumb>
              <SmallThumb $total={thumbImages.length} onClick={handleImageClick(3, thumbImages[3].originalIndex)}>
                <img src={thumbImages[3]?.url} alt={props.property.displayAddress} />
                <HoverOverlay />
                {getOverlay(overlays, 3, totalThumbs, images.length)}
              </SmallThumb>
            </>
          )}
        </SmallThumbs>
      )}
    </Container>
  );
};

export default PropertyThumbTiles;
